import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import {useTimelineStyles} from '../attendanceTimelineHistory';
import clsx from 'clsx';
import RowContainer from './rowContainer';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import DictParser from '../../../../util/dictParser';
import {getAdmissionStartDate} from '../../../../util/patientUtil';
import {getConfig} from '../../../../util/configUtil';
import _ from 'lodash';
import moment from 'moment';
import AgyleLoadingSpinner from '../../../other/agyleLoadingSpinner';
import API from '../../../../app/api';
import {calculateEndDateOfEpisode, getEpisodeType} from '../../../../util/spellsUtil';

const IpTimelineRow = ({admission}) => {
    const classes = useTimelineStyles();
    const {t} = useTranslation();

    // Config
    const dateFormat = getConfig('defaultDateTimeFormat');

    // Consultants
    const [loading, setLoading] = useState(false);
    const [consultants, setConsultants] = useState([]);

    let episodes = [];
    if (admission?.admission?.episodes) {
        episodes = _.orderBy(admission?.admission?.episodes, ['startDate'], ['desc']);
    }

    useEffect(() => {
        const controller = new AbortController();
        if (!_.isEmpty(admission?.admission?.episodes)) {
            setLoading(true);
            const ids = admission?.admission?.episodes?.map(epi => epi.consultant_id);
            API.getConsultantsByIds(ids, controller.signal)
                .then(res => {
                    setConsultants(res?.__data || []);
                })
                .catch(e => {
                    console.error(e);
                })
                .finally(() => setLoading(false));
        }
        return () => {
            controller.abort();
        };
    }, [admission]);

    const getArrival = () => {
        const getCC = () => {
            if (admission?.triage?.chiefComplaint) {
                return DictParser.chiefComplaint(admission?.triage?.chiefComplaint, false);
            } else if (admission?.admission?.initialAEComplaintDescription) {
                return admission?.admission?.initialAEComplaintDescription;
            }
            return t('Not set');
        };

        return (
            <div className={classes.divRow} style={{width: '100%'}}>
                <div style={{width: '20%'}}>
                    <div>
                        {t('Admitted')}
                    </div>

                    <div className={classes.smallText}>
                        {getAdmissionStartDate(admission?.admission)?.format(dateFormat)}
                    </div>
                </div>

                <div style={{width: '80%'}}>
                    <div>
                        <div className={classes.smallText}>
                            {t('Chief Complaint')}
                        </div>

                        <div>
                            {getCC()}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const getConsultant = (id) => {
        const data = _.find(consultants, c => c?.id === id || c?.code === id);
        if (data) {
            const {familyName, firstName} = data;
            return `${firstName} ${familyName}`;
        }
        return id;
    };

    const renderEpisode = (episode, index) => {
        if (!episode) return null;
        const position = episodes.length - index;
        const first = position === episodes.length;

        return (
            <TimelineItem key={episode.startDate} style={{marginTop: first ? 13 : 22}}>
                <TimelineSeparator className={classes.tinySeparator}>
                    <TimelineDot className={clsx(classes.dot, 'small')} variant="filled"/>
                    <TimelineConnector className={classes.connector}/>
                </TimelineSeparator>

                <TimelineContent className={clsx(classes.content, 'margin')}>
                    <RowContainer>
                        <div style={{flexDirection: 'column', width: '100%'}}>
                            <div style={{margin: '5px 0', marginBottom: 15}}>
                                <span className={classes.chipEpisode}>
                                    {`${t('Episode')} ${position} (ID ${episode.episodeNumber || '-'})`}
                                </span>

                                <span className={classes.headerText} style={{marginLeft: 5}}>
                                    {t('Ward:')} {episode?.ward_id || '-'}
                                </span>
                            </div>

                            <div className={classes.divRow} style={{width: '100%'}}>
                                <div style={{width: '20%'}}>
                                    <div className={classes.inpatientSeparator}>
                                        <div>
                                            {t('Start')}
                                        </div>

                                        <div className={classes.smallText}>
                                            {moment(episode.startDate)?.format(dateFormat)}
                                        </div>
                                    </div>

                                    <div>
                                        <div>
                                            {t('End')}
                                        </div>

                                        <div className={classes.smallText}>
                                            {calculateEndDateOfEpisode(episodes, position, admission?.admission?.endDate)}
                                        </div>
                                    </div>
                                </div>

                                <div style={{width: '20%'}}>
                                    <div className={classes.inpatientSeparator}>
                                        {t('Clinician')}
                                    </div>

                                    <div className={classes.inpatientSeparator}>
                                        {t('Speciality')}
                                    </div>

                                    <div>
                                        {t('Type')}
                                    </div>
                                </div>

                                <div style={{width: '60%'}}>
                                    <div className={classes.inpatientSeparator}>
                                        <span style={{fontWeight: 'bold'}}>
                                            {loading &&
                                                <AgyleLoadingSpinner
                                                    disableText
                                                    spinnerSize={14}
                                                    textAlign="left"
                                                />
                                            }
                                            {!loading && getConsultant(episode.consultant_id)}
                                        </span>
                                    </div>

                                    <div className={classes.inpatientSeparator}>
                                        <span style={{fontWeight: 'bold'}}>
                                            {episode?.specialty?.description || '-'}
                                        </span>
                                    </div>

                                    <div>
                                        <span style={{fontWeight: 'bold'}}>
                                            {getEpisodeType(episode, episodes, position)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </RowContainer>
                </TimelineContent>
            </TimelineItem>
        );
    };

    return (
        <>
            {episodes?.map((episode, index) => renderEpisode(episode, index))}

            <TimelineItem style={{marginTop: 22}}>
                <TimelineSeparator>
                    <TimelineDot className={classes.dot} variant="outlined"/>
                </TimelineSeparator>

                <TimelineContent className={clsx(classes.content, 'margin')}>
                    <RowContainer>
                        {getArrival()}
                    </RowContainer>
                </TimelineContent>
            </TimelineItem>
        </>
    );
};

export default IpTimelineRow;